import { useEffect, useRef } from 'react';
import CloseIcon from '../icons/CloseIcon';
import '../../css/modal.scss';

export interface ModalAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
  disabled?: boolean;
}

export interface ModalProps {
  title?: string;
  onClose: () => void;
  children: React.ReactNode;
  actions?: ModalAction[];
  className?: string;
}

export const Modal = ({ title, onClose, children, actions, className = '' }: ModalProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Save previous focus, move focus into dialog, restore on close
  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement;
    dialogRef.current?.focus();
    return () => {
      previousFocusRef.current?.focus();
    };
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Prevent page scroll behind the modal
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Focus trap — keep Tab within the dialog
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const trapTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusable = dialog.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    dialog.addEventListener('keydown', trapTab);
    return () => dialog.removeEventListener('keydown', trapTab);
  }, []);

  return (
    <div
      className="modal-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`modal desktop ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title !== undefined ? 'modal-title' : undefined}
        ref={dialogRef}
        tabIndex={-1}
      >
        <div className="modal-header-container">
          {title !== undefined && (
            <span id="modal-title" className="modal-title">
              {title}
            </span>
          )}
          <button className="close-button" onClick={onClose} aria-label="Close modal">
            <CloseIcon strokeColor="#02222b" strokeWidth={2} width={20} />
          </button>
        </div>

        <div className="modal-content">{children}</div>

        {actions !== undefined && actions.length > 0 && (
          <div className="modal-footer">
            {actions.map((action) => (
              <button
                key={action.label}
                className={`modal-btn${action.variant === 'primary' ? ' cta' : ''}`}
                onClick={action.onClick}
                disabled={action.disabled === true}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
